/**
 * Drag constraint utilities for molecular structures
 *
 * Provides constraint calculations for different dragging scenarios:
 * - Orbit constraint: Single-bond atoms rotate around bonded neighbor
 * - Rigid body: Multi-bond atoms move connected components together
 * - Natural motion: Blended translation/rotation based on drag position
 * - Anchor orbit: Selection-based orbit where dragged side rotates around selected anchor
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';

// ============================================================================
// Geometry Types & Utilities for Natural Motion
// ============================================================================

/**
 * 2D point for geometry calculations (SVG coordinates)
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Calculate Euclidean distance between two points
 */
export function distance(p1: Point2D, p2: Point2D): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate angle from one point to another (radians)
 *
 * Returns angle in range [-PI, PI] where:
 * - 0 = point to the right (+x direction)
 * - PI/2 = point below (+y direction, SVG y-down)
 * - PI or -PI = point to the left (-x direction)
 * - -PI/2 = point above (-y direction)
 */
export function angleFromTo(from: Point2D, to: Point2D): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

/**
 * Rotate a point around a center by given angle (radians)
 *
 * Positive angle = clockwise rotation in SVG coords (y-down)
 * This matches standard SVG rotation direction.
 */
export function rotatePoint(point: Point2D, center: Point2D, angleRadians: number): Point2D {
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);

  // Translate to origin
  const relX = point.x - center.x;
  const relY = point.y - center.y;

  // Rotate
  const rotX = relX * cos - relY * sin;
  const rotY = relX * sin + relY * cos;

  // Translate back
  return {
    x: rotX + center.x,
    y: rotY + center.y,
  };
}

/**
 * Calculate centroid (center of mass) for a set of atoms
 *
 * All positions are assumed to be in SVG coordinates (y-down).
 * For molecular fragments, this provides the geometric center.
 *
 * @param atoms - Array of atoms with positions
 * @returns Centroid point, or origin if empty array
 */
export function calculateFragmentCentroid(atoms: Array<{ id: string; x: number; y: number }>): Point2D {
  if (atoms.length === 0) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;

  for (const atom of atoms) {
    sumX += atom.x;
    sumY += atom.y;
  }

  return {
    x: sumX / atoms.length,
    y: sumY / atoms.length,
  };
}

/**
 * Calculate bounding radius - max distance from centroid to any atom
 *
 * Used to normalize the blend factor calculation.
 * Adds a small buffer (0.5) to ensure atoms at the exact edge
 * still have some translation component.
 *
 * @param atoms - Fragment atoms with positions
 * @param centroid - Pre-calculated centroid
 * @returns Maximum distance from centroid to any atom, plus buffer
 */
export function calculateBoundingRadius(
  atoms: Array<{ id: string; x: number; y: number }>,
  centroid: Point2D
): number {
  let maxDistance = 0;

  for (const atom of atoms) {
    const d = distance({ x: atom.x, y: atom.y }, centroid);
    if (d > maxDistance) {
      maxDistance = d;
    }
  }

  // Add small buffer so atoms at edge still have translation
  return maxDistance + 0.5;
}

/**
 * Calculate quadratic blend factor for natural motion
 *
 * Determines how much rotation vs translation to apply based on
 * how far the pointer is from the fragment's center of mass.
 *
 * Uses quadratic curve: (distance/maxDistance)² for smooth blend
 * that feels translation-heavy near center, rotation-heavy at edges.
 *
 * @param pointerDistanceFromCentroid - Distance from centroid to pointer
 * @param boundingRadius - Maximum distance (defines "edge")
 * @param deadzone - Minimum distance before rotation kicks in (default 2.0)
 * @returns Blend factor 0-1 (0 = pure translation, 1 = pure rotation)
 */
export function calculateBlendFactor(
  pointerDistanceFromCentroid: number,
  boundingRadius: number,
  deadzone: number = 2.0
): number {
  // Below deadzone = pure translation
  if (pointerDistanceFromCentroid <= deadzone) {
    return 0;
  }

  // Normalize distance beyond deadzone
  const effectiveDistance = pointerDistanceFromCentroid - deadzone;
  const effectiveMax = boundingRadius - deadzone;

  if (effectiveMax <= 0) {
    return 0;
  }

  // Quadratic blend: (distance/maxDistance)²
  const normalized = Math.min(effectiveDistance / effectiveMax, 1.0);
  return normalized * normalized;
}

/**
 * Normalize angle to range [-PI, PI]
 *
 * Used to avoid angle discontinuities during rotation.
 */
export function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= 2 * Math.PI;
  while (angle < -Math.PI) angle += 2 * Math.PI;
  return angle;
}

// ============================================================================
// Original Drag Constraint Functions
// ============================================================================

/**
 * Get all bonds that include a specific atom.
 *
 * IMPORTANT: Returns neighbor coordinates in SVG coordinate system (y-down).
 * Workspace uses chemistry coordinates (y-up), so we flip the y-axis.
 *
 * MC-679: Hidden atoms are included by default so they move with their visible
 * parents during drag operations. Pass `visibleOnly: true` when the bond count
 * should reflect only what the user can see (e.g. constraint-type detection).
 *
 * @param visibleOnly - When true, bonds to hidden neighbors are excluded
 */
export function getBondsForAtom(workspace: MolecularWorkspace, atomId: string, visibleOnly = false) {
  const atomBonds: Array<{ bondId: string; neighborId: string; neighborX: number; neighborY: number }> = [];

  for (const bond of workspace.getAllBonds()) {
    const bondId = bond.id;
    if (bond.a1.id === atomId) {
      if (visibleOnly && workspace.getVisibility(bond.a2.id) === VisibilityFlag.Hidden) continue;
      atomBonds.push({
        bondId,
        neighborId: bond.a2.id,
        neighborX: bond.a2.x,
        neighborY: -bond.a2.y, // Y-FLIP: chemistry y-up → SVG y-down
      });
    } else if (bond.a2.id === atomId) {
      if (visibleOnly && workspace.getVisibility(bond.a1.id) === VisibilityFlag.Hidden) continue;
      atomBonds.push({
        bondId,
        neighborId: bond.a1.id,
        neighborX: bond.a1.x,
        neighborY: -bond.a1.y, // Y-FLIP: chemistry y-up → SVG y-down
      });
    }
  }

  return atomBonds;
}

/**
 * Apply orbit constraint for single-bond atoms
 *
 * Constrains the drag target to a circular path around the bonded neighbor,
 * maintaining constant bond length (radius).
 *
 * @param targetX - Desired X position from unconstrained drag
 * @param targetY - Desired Y position from unconstrained drag
 * @param anchorX - X position of the bonded neighbor (orbit center)
 * @param anchorY - Y position of the bonded neighbor (orbit center)
 * @param radius - Bond length to maintain
 * @returns Constrained position on circular orbit
 */
export function applyOrbitConstraint(
  targetX: number,
  targetY: number,
  anchorX: number,
  anchorY: number,
  radius: number
): { x: number; y: number } {
  // Vector from anchor to target
  const dx = targetX - anchorX;
  const dy = targetY - anchorY;

  // Current distance
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Avoid division by zero
  if (distance < 0.0001) {
    return { x: anchorX + radius, y: anchorY }; // Default position to the right
  }

  // Normalize and scale to maintain radius
  const scale = radius / distance;

  return {
    x: anchorX + dx * scale,
    y: anchorY + dy * scale,
  };
}

/**
 * Determine constraint type for an atom based on skeletal connectivity
 *
 * "Skeletal connectivity" counts bonds to the molecular skeleton (excluding hidden H atoms).
 * This ensures all terminal atoms (CH3, OH, NH2, etc.) behave consistently regardless
 * of implicit hydrogens.
 *
 * - 0 skeletal bonds: 'none' (free atom)
 * - 1 skeletal bond: 'orbit' (terminal atom, rotates around skeletal connection)
 * - 2+ skeletal bonds: 'rigid-body' (internal atom, uses natural motion)
 *
 * Examples:
 * - CH3 bonded to one C: 1 skeletal bond → orbit
 * - OH bonded to one C: 1 skeletal bond → orbit
 * - NH2 bonded to one C: 1 skeletal bond → orbit
 * - CH2 bonded to two C's: 2 skeletal bonds → rigid-body
 *
 * @returns 'none' | 'orbit' | 'rigid-body'
 */
export function getConstraintType(
  workspace: MolecularWorkspace,
  atomId: string,
  bondFilter?: (neighborId: string) => boolean,
): 'none' | 'orbit' | 'rigid-body' {
  // Get all bonds including hidden for complete traversal
  const allBonds = getBondsForAtom(workspace, atomId, false);

  // Count bonds to molecular skeleton (exclude hidden hydrogen atoms)
  const skeletalBonds = allBonds.filter((bond) => {
    // Apply optional pre-filter (e.g., restrict to intra-group bonds)
    if (bondFilter && !bondFilter(bond.neighborId)) return false;

    const neighbor = workspace.getAtom(bond.neighborId);
    if (!neighbor) return true; // Keep bond if neighbor not found (shouldn't happen)

    // Exclude bonds to hidden hydrogen atoms (implicit H's)
    const isHiddenHydrogen =
      neighbor.symbol === 'H' && workspace.getVisibility(bond.neighborId) === VisibilityFlag.Hidden;

    return !isHiddenHydrogen; // Include all non-hidden-H bonds
  });

  if (skeletalBonds.length === 0) {
    return 'none'; // Free atom, no skeletal connections
  } else if (skeletalBonds.length === 1) {
    return 'orbit'; // Terminal atom, orbit around skeletal neighbor
  } else {
    return 'rigid-body'; // Internal atom, move as rigid body with natural motion
  }
}

/**
 * Find all atoms in the connected component containing the given atom
 *
 * Uses breadth-first search to traverse bonds and collect all reachable atoms.
 * Hidden atoms are always traversed so they move with the molecule (MC-679),
 * but when `visibleOnly` is true they are excluded from the returned result.
 * Use `visibleOnly: true` for geometry calculations (centroid, bounding radius,
 * translation-group membership) so hidden hydrogens don't skew drag physics.
 * Returns atoms in SVG coordinates (y-down).
 *
 * @param workspace - The molecular workspace
 * @param startAtomId - The atom to start the search from
 * @param visibleOnly - When true, hidden atoms are excluded from the result
 * @returns Array of connected atoms with their IDs and SVG coordinates
 */
export function getConnectedAtoms(
  workspace: MolecularWorkspace,
  startAtomId: string,
  visibleOnly = false
): Array<{ id: string; x: number; y: number }> {
  const visited = new Set<string>();
  const queue: string[] = [startAtomId];
  const result: Array<{ id: string; x: number; y: number }> = [];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId)) continue;
    visited.add(currentId);

    // Get atom data
    const atom = workspace.getAtom(currentId);
    if (!atom) continue;

    // When visibleOnly, skip hidden atoms in the result but still traverse
    // their bonds so all atoms in the component move together (MC-679).
    const isHidden = workspace.getVisibility(currentId) === VisibilityFlag.Hidden;
    if (!visibleOnly || !isHidden) {
      result.push({
        id: currentId,
        x: atom.x,
        y: -atom.y, // Y-FLIP: chemistry y-up → SVG y-down
      });
    }

    // Always traverse neighbors so hidden atoms stay attached to the molecule
    const bonds = getBondsForAtom(workspace, currentId);
    bonds.forEach((bond) => {
      if (!visited.has(bond.neighborId)) {
        queue.push(bond.neighborId);
      }
    });
  }

  return result;
}

// ============================================================================
// Anchor Orbit Functions
// ============================================================================

/**
 * Result of splitting a molecular fragment at an anchor point
 */
export interface AnchorSplitResult {
  /** Atoms on the drag side (will orbit), in SVG coordinates */
  dragSideAtoms: Array<{ id: string; x: number; y: number }>;
  /** Atoms on the fixed side (stay stationary), in SVG coordinates */
  fixedSideAtoms: Array<{ id: string; x: number; y: number }>;
  /** Whether the entire fragment should orbit (no fixed side exists) */
  wholeMoleculeOrbit: boolean;
}

/**
 * Basis data for anchor orbit constraint, captured at drag start
 */
export interface AnchorOrbitBasis {
  /** The anchor atom ID */
  anchorId: string;
  /** The anchor atom position in SVG coordinates */
  anchorPosition: Point2D;
  /** Atoms that will orbit, with their initial geometry relative to anchor */
  orbitingAtoms: Array<{
    id: string;
    /** Distance from anchor (maintained during orbit) */
    radius: number;
    /** Initial angle from anchor in radians */
    initialAngle: number;
  }>;
  /** Whether entire molecule orbits (anchor is terminal or in ring with dragged atom) */
  wholeMoleculeOrbit: boolean;
  /** Initial pointer angle from anchor (for calculating rotation delta) */
  initialPointerAngle: number;
}

/**
 * Get atoms reachable from a start atom via BFS, excluding specified atoms
 *
 * Used to determine which atoms are on a particular "side" of an anchor.
 * Returns atoms in SVG coordinates (y-down).
 *
 * @param workspace - The molecular workspace
 * @param startAtomId - Atom to start BFS from
 * @param excludeAtomIds - Set of atom IDs to NOT traverse through
 * @returns Array of reachable atoms with SVG coordinates
 */
export function getAtomsOnSide(
  workspace: MolecularWorkspace,
  startAtomId: string,
  excludeAtomIds: Set<string>
): Array<{ id: string; x: number; y: number }> {
  const visited = new Set<string>();
  const queue: string[] = [startAtomId];
  const result: Array<{ id: string; x: number; y: number }> = [];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId) || excludeAtomIds.has(currentId)) continue;
    visited.add(currentId);

    const atom = workspace.getAtom(currentId);
    if (!atom) continue;

    result.push({
      id: currentId,
      x: atom.x,
      y: -atom.y, // Y-FLIP: chemistry y-up → SVG y-down
    });

    // Traverse to neighbors (includes hidden atoms - MC-679)
    const bonds = getBondsForAtom(workspace, currentId);
    for (const bond of bonds) {
      if (!visited.has(bond.neighborId) && !excludeAtomIds.has(bond.neighborId)) {
        queue.push(bond.neighborId);
      }
    }
  }

  return result;
}

/**
 * Split a molecular fragment at an anchor point
 *
 * Determines which atoms are on the "drag side" (will orbit) vs "fixed side" (stay put).
 *
 * Algorithm:
 * 1. BFS from dragged atom, excluding anchor → finds "drag side" atoms
 * 2. BFS from anchor, excluding drag side → finds "fixed side" atoms
 * 3. If drag side contains all fragment atoms (except anchor), it's a whole-molecule orbit
 *
 * Special cases for whole-molecule orbit:
 * - Anchor has only one bond direction toward the dragged atom
 * - Anchor is in a ring that includes the dragged atom (can't split a ring)
 *
 * @param workspace - The molecular workspace
 * @param anchorId - The selected anchor atom
 * @param draggedAtomId - The atom being dragged
 * @returns Split result with drag side, fixed side, and whole-molecule flag
 */
export function splitFragmentAtAnchor(
  workspace: MolecularWorkspace,
  anchorId: string,
  draggedAtomId: string
): AnchorSplitResult {
  // Get all atoms in the fragment containing the anchor
  const fragmentAtoms = getConnectedAtoms(workspace, anchorId);
  const fragmentAtomIds = new Set(fragmentAtoms.map((a) => a.id));

  // Verify dragged atom is in same fragment
  if (!fragmentAtomIds.has(draggedAtomId)) {
    // Not in same fragment - return empty result
    return {
      dragSideAtoms: [],
      fixedSideAtoms: [],
      wholeMoleculeOrbit: false,
    };
  }

  // BFS from dragged atom, excluding anchor → drag side
  const dragSideAtoms = getAtomsOnSide(workspace, draggedAtomId, new Set([anchorId]));
  const dragSideIds = new Set(dragSideAtoms.map((a) => a.id));

  // Check if drag side contains all atoms except anchor
  // If so, it's a whole-molecule orbit (anchor is terminal or all paths lead through drag side)
  const nonAnchorAtoms = fragmentAtoms.filter((a) => a.id !== anchorId);
  const wholeMoleculeOrbit = dragSideAtoms.length === nonAnchorAtoms.length;

  if (wholeMoleculeOrbit) {
    return {
      dragSideAtoms,
      fixedSideAtoms: [],
      wholeMoleculeOrbit: true,
    };
  }

  // BFS from anchor, excluding drag side atoms → fixed side
  // Note: anchor itself is not on either "side", it's the pivot point
  const fixedSideAtoms = getAtomsOnSide(workspace, anchorId, dragSideIds);
  // Remove anchor from fixed side (it's the pivot, not a moving atom)
  const fixedSideWithoutAnchor = fixedSideAtoms.filter((a) => a.id !== anchorId);

  return {
    dragSideAtoms,
    fixedSideAtoms: fixedSideWithoutAnchor,
    wholeMoleculeOrbit: false,
  };
}

/**
 * Calculate anchor orbit basis from anchor position and orbiting atoms
 *
 * For each orbiting atom, calculates:
 * - radius: distance from anchor (will be maintained during orbit)
 * - initialAngle: angle from anchor to atom (rotation will be applied relative to this)
 *
 * @param anchorPosition - Anchor atom position in SVG coordinates
 * @param orbitingAtoms - Atoms that will orbit around anchor
 * @returns Array of orbit data for each atom
 */
export function calculateAnchorOrbitAtoms(
  anchorPosition: Point2D,
  orbitingAtoms: Array<{ id: string; x: number; y: number }>
): AnchorOrbitBasis['orbitingAtoms'] {
  return orbitingAtoms.map((atom) => ({
    id: atom.id,
    radius: distance(anchorPosition, { x: atom.x, y: atom.y }),
    initialAngle: angleFromTo(anchorPosition, { x: atom.x, y: atom.y }),
  }));
}

/**
 * Apply anchor orbit rotation to get new positions for orbiting atoms
 *
 * Rotates each orbiting atom around the anchor by the angle delta.
 * Maintains each atom's distance from anchor (radius).
 *
 * @param basis - The anchor orbit basis with initial geometry
 * @param angleDelta - Rotation amount in radians (current - initial pointer angle)
 * @returns New positions for all orbiting atoms in SVG coordinates
 */
export function applyAnchorOrbitRotation(
  basis: AnchorOrbitBasis,
  angleDelta: number
): Array<{ id: string; x: number; y: number }> {
  return basis.orbitingAtoms.map((atom) => {
    const newAngle = atom.initialAngle + angleDelta;
    return {
      id: atom.id,
      x: basis.anchorPosition.x + atom.radius * Math.cos(newAngle),
      y: basis.anchorPosition.y + atom.radius * Math.sin(newAngle),
    };
  });
}
